import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db, doc, getDoc, collection, getDocs, query, where } from '../firebase';
import { claimMembership, getAccessibleMemberships, normalizeEmail } from '../utils/membership';
import '../App.css';

const initials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
};

const TeamCard = ({ team, editable = false }) => (
  <Link
    to={`/t/${team.slug || team.id}`}
    className="team-card"
    title={team.name}
  >
    <div
      className="team-card-avatar"
      style={editable ? { background: 'rgba(0,245,160,0.15)', color: '#00f5a0' } : undefined}
    >
      {initials(team.name)}
    </div>
    <div className="team-card-body">
      <h3 className="team-card-name">{team.name || 'Bez imena'}</h3>
      <div className="team-card-meta">
        <span className="team-card-slug">@{team.slug || team.id}</span>
        {(team.passwordEnabled || team.visibility === 'private') ? (
          <span className="team-card-badge badge-locked">Pod lozinkom</span>
        ) : (
          <span className={`team-card-badge badge-${team.visibility}`}>
            {team.visibility === 'public' ? 'Javno' : 'Privatno'}
          </span>
        )}
        {team.myRole === 'pending-player' && (
          <span className="team-card-badge" style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24', borderColor: 'transparent' }}>
            Pozvan
          </span>
        )}
      </div>
    </div>
  </Link>
);

const MyDashboard = ({ user }) => {
  const [myTeams, setMyTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userUid = user?.uid;
  const userEmail = user?.email;

  useEffect(() => {
    if (!userUid) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchMyTeams = async () => {
      try {
        if (userUid && userEmail) {
          try {
            await claimMembership(userUid, normalizeEmail(userEmail));
          } catch (claimError) {
            console.warn('MyDashboard claim retry nije uspio:', claimError);
          }
        }

        const ownerQ = query(
          collection(db, 'teams'),
          where('ownerUid', '==', userUid)
        );

        const managerQ = query(
          collection(db, 'teams'),
          where('managerUids', 'array-contains', userUid)
        );

        const [ownerSnap, managerSnap, memberships] = await Promise.all([
          getDocs(ownerQ),
          getDocs(managerQ).catch(() => ({ docs: [] })),
          getAccessibleMemberships({ uid: userUid, email: userEmail }).catch((e) => {
            console.warn(
              'getAccessibleMemberships nije uspio (nastavljamo bez member timova):',
              e
            );
            return [];
          }),
        ]);

        if (cancelled) return;

        const byId = new Map();
        ownerSnap.docs.forEach((d) => {
          byId.set(d.id, { id: d.id, myRole: 'owner', ...d.data() });
        });

        managerSnap.docs.forEach((d) => {
          if (!byId.has(d.id)) {
            byId.set(d.id, { id: d.id, myRole: 'owner', ...d.data() });
          }
        });

        const memberTeamIds = memberships
          .map((m) => m.teamId)
          .filter((id) => id && !byId.has(id));

        if (memberTeamIds.length > 0) {
          const teamDocs = await Promise.all(
            memberTeamIds.map((id) => getDoc(doc(db, 'teams', id)))
          );
          if (cancelled) return;

          teamDocs.forEach((snap) => {
            if (!snap.exists()) return;
            const membership = memberships.find((m) => m.teamId === snap.id);
            byId.set(snap.id, {
              id: snap.id,
              myRole: membership?.claimed ? 'player' : 'pending-player',
              ...snap.data(),
            });
          });
        }

        // Dohvati timove iz legacy players kolekcije
        // slugs kolekcija je world-readable — koristimo je za dohvat team ID-ova
        if (userEmail) {
          const slugsSnap = await getDocs(collection(db, 'slugs')).catch(() => ({ docs: [] }));
          const allTeamIds = Array.from(new Set(
            slugsSnap.docs.map((d) => d.data()?.teamId).filter(Boolean)
          ));

          const playerChecks = await Promise.all(
            allTeamIds.map((teamId) =>
              getDoc(doc(db, 'teams', teamId, 'players', userEmail)).catch(() => null)
            )
          );
          if (cancelled) return;

          const newTeamIds = playerChecks
            .map((snap, i) => ({ snap, teamId: allTeamIds[i] }))
            .filter(({ snap, teamId }) => snap && snap.exists() && !byId.has(teamId))
            .map(({ teamId }) => teamId);

          const teamDataFetches = newTeamIds.map((teamId) =>
            getDoc(doc(db, 'teams', teamId)).catch(() => null)
          );
          const teamDatas = await Promise.all(teamDataFetches);
          if (cancelled) return;

          newTeamIds.forEach((teamId, i) => {
            const teamSnap = teamDatas[i];
            if (!teamSnap || !teamSnap.exists()) return;
            byId.set(teamId, { id: teamId, myRole: 'player', ...teamSnap.data() });
          });

          // Za timove gdje je korisnik već owner/manager, upgradaj ulogu na owner+player
          playerChecks.forEach((snap, i) => {
            if (!snap || !snap.exists()) return;
            const teamId = allTeamIds[i];
            const existing = byId.get(teamId);
            if (!existing) return;
            if (existing.myRole === 'owner') {
              existing.myRole = 'owner+player';
            }
          });
        }

        memberships.forEach((membership) => {
          const existing = byId.get(membership.teamId);
          if (!existing || existing.myRole !== 'owner') return;
          if (membership.role === 'owner') return;
          existing.myRole = membership.claimed
            ? 'owner+player'
            : 'owner+pending-player';
        });

        const list = Array.from(byId.values()).sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
        setMyTeams(list);
      } catch (e) {
        console.error('Greška pri dohvaćanju timova korisnika:', e);
        if (!cancelled) {
          setError(e.message || 'Greška pri dohvaćanju timova.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMyTeams();

    return () => {
      cancelled = true;
    };
  }, [userUid, userEmail]);

  const playerTeams = myTeams.filter((t) =>
    t.myRole === 'player' || t.myRole === 'pending-player' || t.myRole === 'owner+player' || t.myRole === 'owner+pending-player'
  );
  const editableTeams = myTeams.filter((t) => t.myRole?.includes('owner'));

  if (loading) {
    return <div className="loading-screen">Učitavanje tvojih timova...</div>;
  }

  if (error) {
    return (
      <div className="container">
        <div className="empty-state">Greška: {error}</div>
      </div>
    );
  }

  if (myTeams.length === 0) {
    return (
      <div className="container">
        <section className="hero">
          <span className="hero-eyebrow">Dobrodošao na StatArena</span>
          <h1 className="hero-title">Kreiraj svoj prvi tim</h1>
          <p className="hero-subtitle">
            Još nemaš svoj tim. Pokreni svoju ekipu za par klikova - dodaj igrače,
            zakaži termin i aplikacija će se pobrinuti za ostalo.
          </p>
          <div className="hero-actions">
            <Link to="/create-team" className="btn btn-primary btn-lg">
              + Kreiraj tim
            </Link>
            <Link to="/directory" className="btn btn-secondary btn-lg">
              Pogledaj druge timove
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="container">
      <section className="hero">
        <span className="hero-eyebrow">Tvoj prostor</span>
        <h1 className="hero-title">Odaberi tim</h1>
        <p className="hero-subtitle">
          Klikni na tim da otvoriš njegov raspored, statistiku i rang listu.
        </p>
      </section>

      {playerTeams.length > 0 && (
        <>
          <div className="section-header">
            <h2>Moji timovi</h2>
            <span className="section-badge">{playerTeams.length}</span>
          </div>
          <div className="team-grid">
            {playerTeams.map((team) => (
              <TeamCard key={team.id} team={team} />
            ))}
          </div>
        </>
      )}

      {editableTeams.length > 0 && (
        <>
          <div className="section-header" style={{ marginTop: playerTeams.length > 0 ? 40 : 0 }}>
            <h2>Moji timovi za uređivanje</h2>
            <span className="section-badge">{editableTeams.length}</span>
          </div>
          <div className="team-grid">
            {editableTeams.map((team) => (
              <TeamCard key={team.id} team={team} editable />
            ))}
          </div>
        </>
      )}

      <div style={{ marginTop: 32, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Link to="/create-team" className="btn btn-primary">
          + Kreiraj novi tim
        </Link>
        <Link to="/directory" className="btn btn-secondary">
          Svi javni timovi
        </Link>
      </div>
    </div>
  );
};

export default MyDashboard;
